import { useContext, useEffect, useMemo } from "react"

import { useSelector } from "react-redux"

import { selectUserId } from "selectors/user"
import { teeupUserStatusKeys } from "@configs/enums"
import { NewTeeupPageContext } from "@contexts/NewTeeupPageContext"
import { selectTeeupPeople } from "@selectors/teeups"
import DefaultModal from "@components/ui/DefaultModal"
import { NewTeeupSettings } from "components"
import { useScreenWidth } from "hooks"
import { useTeeupPeopleStore } from "stores/teeupPeopleStore"

import { useFetchTeeUps } from "@components/TeeUpsNd/hooks/useFetchTeeUps"
import GameplanSection from "../NewTeeupGameplan/molecules/GameplanSection"
import OverviewConversation from "../TeeUpsOverview/OverviewConversation"
import People from "../TeeupPeople"
import { AgendaSection, TasksSection } from "./AgendaTasksSections"
import { RescheduleBanner } from "./RescheduleBanner"

import { ContentWrapper, LeftSide } from "./teeupPageContentStyles"
import { selectActiveTeeupId } from "@selectors/activeTeeup"

const attendanceStatuses = new Set([
  teeupUserStatusKeys.going,
  teeupUserStatusKeys.onmyway,
  teeupUserStatusKeys.runningLate,
  teeupUserStatusKeys.arrived,
])

// CANONICAL-DETAIL (2026-07-04, QJ FINAL RULING): reverts the 2026-07-03 TEEUP-DETAIL-BODYLAYOUT
// rebuild's LEFT-column order and card structure back to the PRE-ALESSANDRA layout -- the
// canonical layouts are ONLY the mobile Figma prototype and the pre-Ale web structure; Ale's
// restyle (her Game-plan-first, side-by-side ordering below) is not a design target. LEFT column
// top->bottom is now: People card (title "People · N Total · N Attending", avatar row, status
// control right -- unchanged internals, see TeeupPeople/TitleContent) -> Game plan card (ONE
// card, WHEN/WHERE stacked vertically, see GameplanSection.tsx) -> Agenda -> Tasks. RIGHT column:
// Conversation (unchanged). Settled QJ features riding on top of this structure, unchanged: the
// 6-tab Conversation bar, the sorted/drag-scroll People avatar strip, the blue token palette, and
// green Decided chips on the Game plan rows (WhenWhereCard.tsx).
//
// TEEUP-DETAIL-BODYLAYOUT 2026-07-03 (superseded above): rebuilt to match Ale's prototype
// (`coowe.html` Ze()) EXACTLY -- QJ's direct correction that the 2026-07-03 TEEUP-INSIDE Part 2
// pass did not actually match her button layout. LEFT column top->bottom, verbatim order from her
// code: Game plan (When+Where SIDE BY SIDE in one card, shared "See past suggestions" toggle) ->
// People (Organizer + avatars + Multitimezone row, expands in place) -> Agenda (ALWAYS expanded,
// no collapse) -> Tasks (ALWAYS expanded, All|Me pills, no collapse). RIGHT column: Conversation
// (unchanged, already correct).
// JUDGMENT CALL: the "What/Why" purpose-statement card (`WhatWhy.tsx`, added in Part 2) is REMOVED
// from this flow -- Ale's left column has no such section and the spec enumerates exactly these 4
// items "top->bottom", none of them What/Why. `WhatWhy.tsx` is left in the tree, unreferenced, in
// case a future ticket wants to reintroduce the teeup.description surface elsewhere (e.g. Settings
// or a header subtitle) -- not deleted, just not part of this body layout.
export const TeeupPageContent = () => {
  const { handleNudgesAndMentions } = useFetchTeeUps()

  const { isSettings, setIsSettings } = useContext(NewTeeupPageContext)
  const userId = useSelector(selectUserId)
  const isPeopleOpen = useTeeupPeopleStore(state => state.isCollapsed)
  const messages = useSelector(state => state?.activeTeeup?.messages)
  const setIsPeopleOpen = useTeeupPeopleStore(state => state.setIsCollapsed)
  const clearPeopleState = useTeeupPeopleStore(state => state.clear)
  const teeupId = useSelector(selectActiveTeeupId)
  const teeupPeople = useSelector(selectTeeupPeople)

  // CANONICAL-DETAIL: the pre-Ale People card title reads "People · N Total · N Attending" --
  // same counting logic TeeupPeople/index.jsx already runs for its own Multitimezone/isOrganizer
  // memo (attendanceStatuses set kept identical), computed here too since the section label now
  // lives one level up from that component (same pattern the 2026-07-03 fidelity pass already
  // established for keeping the label OUT of the row itself).
  const { peopleTotal, peopleAttending } = useMemo(() => {
    const list = Object.values(teeupPeople || {})
    const attending = list.reduce(
      (count, item) => (attendanceStatuses.has(item.status) ? count + 1 : count),
      0
    )
    return { peopleTotal: list.length, peopleAttending: attending }
  }, [teeupPeople])

  const { isSmallSize } = useScreenWidth()

  useEffect(() => {
    if (messages?.length) {
      handleNudgesAndMentions({ [teeupId]: messages }, userId)
    }

    return () => {
      clearPeopleState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePeopleClick = () => {
    setIsPeopleOpen(!isPeopleOpen)
  }

  // FIX 2026-07-03 (QJ-flagged interaction-pattern bug): the Settings gear used to REPLACE the
  // entire page body with <NewTeeupSettings /> (a body-swap navigation, not a popup) -- every
  // other in-detail action (Suggest When/Where, AI suggest, etc.) opens as an overlay popup via
  // <DefaultModal>, mounted alongside the normal body, never swapping it out. The gear is now
  // consistent with that same pattern: the body always renders normally, and Settings opens as a
  // <DefaultModal> overlay (same component the Suggest modals use) controlled by isSettings.
  // NewTeeupSettings already has its own internal Header with a back/close action wired to
  // setIsSettings(false) (see molecules/Header.js), so closing works identically to before.
  return (
    <>
      <ContentWrapper>
        <LeftSide>
          {/* CANONICAL-DETAIL: "PEOPLE" label sits ABOVE the card (kept from the 2026-07-03
              fidelity pass's fix -- inline labels ate row width), now compounded with the
              pre-Ale total/attending counts per QJ's canonical-image title.
              People Card spec reconciliation (d45923fee2264c559a5512601920958c, 2026-07-04):
              the written spec wants an invited -> going count (e.g. "3 of 8 going"). Rather than
              restructure this canonical-detail header (own layout/testid, relied on elsewhere),
              kept the canonical "People · N Total · N X" shape and swapped the label from
              "Attending" to "Going" -- same underlying attendanceStatuses count (going/on my
              way/running late/arrived), just worded to match the spec's going-count semantics. */}
          <div className="mx-0.5 mb-2 flex items-center justify-between">
            <span
              data-testid="teeup-detail-people-title"
              className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9AA2B1]">
              People · {peopleTotal} Total · {peopleAttending} Going
            </span>
          </div>

          <People onClick={handlePeopleClick} />

          {/* HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09): reschedule-dependency banner sits above the
              Game Plan card (brief anchor: mounts "at/above the Game Plan card"). Renders nothing
              when tasks capability is off or there is no incomplete reschedule-linked task -- see
              RescheduleBanner.tsx for the resilience contract. */}
          <div className="mt-3">
            <RescheduleBanner />
            <GameplanSection />
          </div>

          <AgendaSection />

          <TasksSection />

          {isSmallSize && (
            <div className="overflow-hidden bg-white pt-2">
              <OverviewConversation newDesign />
            </div>
          )}
        </LeftSide>

        {/* PRE-ALE-MATCH (2026-07-04, QJ correction, reverts the 2026-07-03 DETAIL-FIDELITY
            pass's `.chatpanel` treatment below): the canonical layout (rule 17c) puts the
            Conversation in its OWN CONTAINED WHITE PANEL, the same card chrome as the left
            column's People/Game-plan cards -- white ground, bordered/rounded, subtle shadow --
            not a borderless panel flush with the page background. Matches the pre-Ale wrapper
            (`rounded-2xl border border-[#E7E7EA] bg-white overflow-hidden`, see git history at
            `2e922a76`) re-expressed with this pass's current tokens (`#E6EBF3` family,
            `rounded-[13px]`, the same hairline shadow the left cards use). `overflow-hidden`
            keeps the rounded corners clipping the tab bar/messages/composer; `.nd-conversation`
            (OverviewConversation's newDesign class) is `height:100%` + flex column + the
            messages area (DiscussionCard) grows while ConversationBottom (the composer) is
            pinned at the bottom via `justify-content: space-between` -- unchanged, this only
            restores the OUTER card chrome those already rely on.
            SUPERSEDED comment (2026-07-03 DETAIL-FIDELITY, do not reinstate): "Ale `.chatpanel`
            (coowe.html): NOT a bordered/rounded card like the left-column sections -- a plain
            panel separated only by a left border, flush with the page background (border-left
            1px solid var(--line-2) #EFF3F9, padding-left 30px)." Ale's restyle is not a design
            target (rule 17c). */}
        {!isSmallSize && (
          <div
            data-testid="teeup-detail-conversation"
            className="flex min-w-0 flex-col overflow-hidden rounded-[13px] border border-[#E6EBF3] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
            <OverviewConversation newDesign />
          </div>
        )}

        {/* WINDOW-ARCH 2026-07-03: CooweAI now mounts ONCE at the app shell (App.js). */}
      </ContentWrapper>

      <DefaultModal
        open={isSettings}
        onClose={() => setIsSettings(false)}
        responsiveBreakpoint={isSmallSize}>
        <div
          data-testid="teeup-detail-settings-modal"
          style={{ maxHeight: "85vh", overflowY: "auto" }}>
          <NewTeeupSettings />
        </div>
      </DefaultModal>
    </>
  )
}
